import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchNegociacaoIndividualAgents } from '@/features/negociacaoIndividualWizard';
import { registrarAcao } from '@/shared/utils/reportAction';
import type { RootState, AppDispatch } from '@/store';

import type {
  Config,
  DespachoDaFeature,
  EstadoSimulacao,
  ErroDaSimulacao,
  MotivoDeEncerramentoPeloWeb,
  DesfechoDoInicio,
  MotivoDeInicio,
} from '../../types';
import { intencaoReaproveitavel } from '../../utils/intencaoDeComando';
import {
  intencaoRegistrada,
  agenteSelecionado,
  configuracaoAlteradaDetectada,
  dadoAlterado,
} from '../eventosDoStore';
import { paraErroDaSimulacao, registrarFalha } from '../falhasDaSimulacao';
import { selectEstadoDaSimulacao as estadoDaFeature } from '../selectors';
import { servicosDaSimulacao } from '../servicosDaSimulacao';
import { simulacaoService } from '../services';
import { PRIMEIRA_PAGINA_DE_AGENTES } from '../../utils/paginaDeAgentes';

const CODIGOS_QUE_RECARREGAM_AGENTES = new Set(['AGENTE_INDISPONIVEL', 'AGENTE_NAO_ENCONTRADO']);

export const buscarAcessoSimulacao = createAsyncThunk<
  { escritorioId: string; podeUsar: boolean },
  string,
  Config
>('negociacaoSimulacao/buscarAcesso', async (escritorioId, { rejectWithValue }) => {
  try {
    const { podeUsar } = await simulacaoService.buscarAcesso();
    return { escritorioId, podeUsar: podeUsar === true };
  } catch (erro) {
    const falha = paraErroDaSimulacao(erro);
    // Sem EDITOR em NEGOCIACAO o item só não aparece (CA-1): é resposta, não falha.
    if (falha.status === 403) return { escritorioId, podeUsar: false };
    registrarFalha('buscarAcesso', erro, falha, {});
    return rejectWithValue(falha);
  }
});

/** `marco` é o da tela quando a leitura saiu: o slice descarta a que chegou depois de uma troca. */
export const buscarSimulacaoAtual = createAsyncThunk<
  { estado: EstadoSimulacao | null; marco: number },
  void,
  Config
>(
  'negociacaoSimulacao/buscarAtual',
  async (_, { getState, rejectWithValue }) => {
    const { marcoDaSimulacao, falhaDaSincronizacao } = estadoDaFeature(getState());
    try {
      const { buscarAtual } = servicosDaSimulacao(getState());
      return { estado: await buscarAtual(), marco: marcoDaSimulacao };
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      if (!falhaDaSincronizacao.atual) registrarFalha('buscarAtual', erro, falha, {});
      return rejectWithValue(falha);
    }
  },
  { condition: (_, { getState }) => !estadoDaFeature(getState()).trocandoEscritorio },
);

/**
 * 409 SIMULACAO_ENCERRADA: o flow (ou outra aba) encerrou a simulação entre a última leitura e o
 * clique. Reler a atual traz o status ENCERRADA, e a entrada se desabilita pelo estado do core.
 */
const relerSeEncerrada = (
  falha: ErroDaSimulacao,
  dispatch: (acao: ReturnType<typeof buscarSimulacaoAtual>) => unknown,
) => {
  if (falha.codigo === 'SIMULACAO_ENCERRADA') void dispatch(buscarSimulacaoAtual());
};

/**
 * 409 CONFIGURACAO_ALTERADA (fluxo antigo salvo depois do início, no admin): a configuração
 * alterada não roda, e só o Reiniciar sai disso (decisão provisória 4). Não quer dizer que nada
 * rodou: fluxo do mapa salvo é recusado antes do consumo, mas fluxo fora do mapa (a função passou
 * a apontar para outro) é recusado de dentro da execução, e a conversa pode ter o que os nós
 * anteriores fizeram. Reler a atual traz o `configuracaoAlterada` do core.
 */
export const relerSeConfiguracaoAlterada = (
  falha: ErroDaSimulacao,
  simulacaoId: string,
  dispatch: DespachoDaFeature,
): boolean => {
  if (falha.codigo !== 'CONFIGURACAO_ALTERADA') return false;
  dispatch(configuracaoAlteradaDetectada(simulacaoId));
  void dispatch(buscarSimulacaoAtual());
  return true;
};

export const reagirARecusaDoComando = (
  falha: ErroDaSimulacao,
  simulacaoId: string,
  dispatch: DespachoDaFeature,
) => {
  if (!relerSeConfiguracaoAlterada(falha, simulacaoId, dispatch)) relerSeEncerrada(falha, dispatch);
};

/**
 * 409 SIMULACAO_EM_CRIACAO é o início concorrente (outra aba, clique duplo que escapou do
 * `condition`): a simulação que venceu é a atual, e a tela segue com ela (CA-4).
 */
export const recuperarSimulacaoEmCriacao = async (
  falha: ErroDaSimulacao,
  buscarAtual: () => Promise<EstadoSimulacao | null>,
) => {
  if (falha.codigo !== 'SIMULACAO_EM_CRIACAO') return null;
  return buscarAtual().catch(() => null);
};

export const encerrarSimulacao = createAsyncThunk<
  EstadoSimulacao,
  { simulacaoId: string; motivo: MotivoDeEncerramentoPeloWeb },
  Config
>(
  'negociacaoSimulacao/encerrar',
  async ({ simulacaoId, motivo }, { getState, rejectWithValue }) => {
    try {
      return await servicosDaSimulacao(getState()).encerrar(
        simulacaoId,
        motivo,
        crypto.randomUUID(),
      );
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      registrarFalha('encerrar', erro, falha, { simulacaoId });
      return rejectWithValue(falha);
    }
  },
);

/**
 * Agente trocado depois do início é o motivo, mesmo que um dado tenha mudado em seguida: a
 * simulação criada era de outro agente.
 */
const motivoDaAlteracao = (
  state: RootState,
  flowIdDoInicio: string,
): MotivoDeEncerramentoPeloWeb => {
  const { formulario, ultimaAlteracaoDoFormulario } = estadoDaFeature(state);
  if (formulario.flowId !== flowIdDoInicio) return 'AGENTE_TROCADO';
  return ultimaAlteracaoDoFormulario ?? 'DADOS_ALTERADOS';
};

/**
 * O agente ou um dado mudou com o início em voo (CA-5): a simulação saiu com o formulário
 * anterior. Ela não é assumida e é encerrada como a troca encerraria uma ATIVA; o formulário fica
 * com os valores novos e pede outro Iniciar.
 */
export const conferirFormularioDoInicio = (
  estado: EstadoSimulacao,
  inicio: { versao: number; flowId: string; escritorioId: string | null },
  dispatch: (acao: ReturnType<typeof encerrarSimulacao>) => unknown,
  getState: () => RootState,
): DesfechoDoInicio => {
  if (estadoDaFeature(getState()).escritorioId !== inicio.escritorioId)
    return { estado, descartada: true };
  if (estadoDaFeature(getState()).versaoDoFormulario === inicio.versao) {
    return { estado, descartada: false };
  }
  const motivo = motivoDaAlteracao(getState(), inicio.flowId);
  registrarAcao('negociacaoSimulacao.inicioDescartado', {
    simulacaoId: estado.id,
    flowId: inicio.flowId,
    motivo,
  });
  if (estado.status === 'ATIVA')
    void dispatch(encerrarSimulacao({ simulacaoId: estado.id, motivo }));
  return { estado, descartada: true };
};

/**
 * Clicar de novo em Iniciar com o formulário igual, depois de uma falha de desfecho desconhecido,
 * é a mesma intenção: a chave guardada volta, e o core devolve a simulação que talvez já tenha
 * criado em vez de criar outra (CA-4). O slice só guarda a intenção do início enquanto o desfecho
 * é desconhecido; sucesso ou recusa 4xx a descartam, e o próximo clique sai com chave nova.
 */
export const iniciarSimulacao = createAsyncThunk<
  DesfechoDoInicio,
  { motivo: MotivoDeInicio },
  Config
>(
  'negociacaoSimulacao/iniciar',
  async ({ motivo }, { dispatch, getState, rejectWithValue }) => {
    const { formulario, intencoes, versaoDoFormulario, escritorioId } = estadoDaFeature(getState());
    const flowId = formulario.flowId ?? '';
    const inicio = { versao: versaoDoFormulario, flowId, escritorioId };
    const dados = { flowId, parametros: formulario.valores, motivo };
    const intencao = intencaoReaproveitavel(intencoes, 'iniciar', dados);
    dispatch(intencaoRegistrada({ tipo: 'iniciar', intencao }));
    try {
      const estado = await simulacaoService.iniciar(dados, intencao.chave);
      registrarAcao('negociacaoSimulacao.iniciada', { simulacaoId: estado.id, flowId, motivo });
      return conferirFormularioDoInicio(estado, inicio, dispatch, getState);
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      if (estadoDaFeature(getState()).escritorioId !== escritorioId) return rejectWithValue(falha);
      const emCriacao = await recuperarSimulacaoEmCriacao(falha, simulacaoService.buscarAtual);
      if (emCriacao) return conferirFormularioDoInicio(emCriacao, inicio, dispatch, getState);
      // Agente inativado ou permissão revogada entre a listagem e o início (CA-2).
      if (
        falha.status === 403 ||
        (falha.codigo && CODIGOS_QUE_RECARREGAM_AGENTES.has(falha.codigo))
      ) {
        void dispatch(fetchNegociacaoIndividualAgents(PRIMEIRA_PAGINA_DE_AGENTES));
      }
      registrarFalha('iniciar', erro, falha, { flowId });
      return rejectWithValue(falha);
    }
  },
  {
    condition: (_, { getState }) => {
      const { iniciando, formulario, superficie } = estadoDaFeature(getState());
      return superficie === 'ESCRITORIO' && !iniciando && formulario.flowId !== null;
    },
  },
);

/**
 * Trocar o agente com a simulação ATIVA a encerra no core e exige novo Iniciar (CA-5). A troca
 * vale na hora, mesmo se o encerramento falhar: o próximo início encerra a antiga como
 * NOVA_SIMULACAO, e os valores do agente anterior já saíram do formulário.
 */
export const trocarAgenteDaSimulacao =
  (flowId: string | null) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { simulacao, formulario } = estadoDaFeature(getState());
    if (flowId === formulario.flowId) return;
    dispatch(agenteSelecionado(flowId));
    if (simulacao?.status !== 'ATIVA') return;
    return dispatch(encerrarSimulacao({ simulacaoId: simulacao.id, motivo: 'AGENTE_TROCADO' }));
  };

export const alterarDadoDaSimulacao =
  (nome: string, valor: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { simulacao } = estadoDaFeature(getState());
    dispatch(dadoAlterado({ nome, valor }));
    if (simulacao?.status !== 'ATIVA') return;
    return dispatch(encerrarSimulacao({ simulacaoId: simulacao.id, motivo: 'DADOS_ALTERADOS' }));
  };
